import React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import clsx from 'clsx';
import { createStyles, makeStyles } from '@material-ui/core';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import { QueryOrderSettings } from '@sprinx/query-builder';
import { StandardDivProps } from '@sprinx/react-mui-components';
import { currencyState, localeState } from '../../../api/appState';
import { customerPriceGroupState } from '../../../api/customer/customer';
import { catalogueOrderByState } from '../../../api/products/catalogue';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

type CataloguePageListToolbarOrderingPickerProps = StandardDivProps<CataloguePageListToolbarOrderingPickerClassKey>;

type CataloguePageListToolbarOrderingPickerClassKey = 'root' | 'highligtedButton';

const useStyles = makeStyles(
  (/* theme: Theme */) =>
    createStyles<CataloguePageListToolbarOrderingPickerClassKey, {}>({
      root: {},
      highligtedButton: {
        color: '#000',
        borderColor: '#FFF',
        border: 'none',
        fontSize: '16px',
        textTransform: 'none',
        '&.Mui-selected': {
          backgroundColor: '#FFF',
          color: '#042751',
          textDecoration: 'underline',
        },
      },
    }),
  { name: 'CataloguePageListToolbarOrderingPicker' },
);

const CataloguePageListToolbarOrderingPicker = React.forwardRef<
  HTMLDivElement,
  CataloguePageListToolbarOrderingPickerProps
>(({ className, classes: pClasses }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const OrderByMap = useOrderByMap();
  const t = useTranslateWithFallback();

  const [catalogueOrder, setCatalogueOrder] = useRecoilState(catalogueOrderByState);

  return (
    <div className={clsx(classes.root, className)} ref={ref}>
      <div className={classes.root}>
        <ToggleButtonGroup
          value={JSON.stringify(catalogueOrder)}
          onChange={(evnt: any, v: any) => setCatalogueOrder(JSON.parse(v))}
          exclusive
          size='small'
        >
          <ToggleButton
            className={classes.highligtedButton}
            value={JSON.stringify(OrderByMap['skuAsc'])}
            disabled={JSON.stringify(catalogueOrder) === JSON.stringify(OrderByMap['skuAsc'])}
            aria-label='podle kódu'
          >
            {t('cataloguePage/orderBy/code')}
          </ToggleButton>
          <ToggleButton
            className={classes.highligtedButton}
            value={JSON.stringify(OrderByMap['nameAsc'])}
            disabled={JSON.stringify(catalogueOrder) === JSON.stringify(OrderByMap['nameAsc'])}
            aria-label='order by name'
          >
            {t('cataloguePage/orderBy/name')}
          </ToggleButton>
          <ToggleButton
            className={classes.highligtedButton}
            value={JSON.stringify(OrderByMap['priceAsc'])}
            disabled={JSON.stringify(catalogueOrder) === JSON.stringify(OrderByMap['priceAsc'])}
            aria-label='price asc'
          >
            {t('cataloguePage/orderBy/priceAsc')}
          </ToggleButton>
          <ToggleButton
            className={classes.highligtedButton}
            value={JSON.stringify(OrderByMap['priceDesc'])}
            disabled={JSON.stringify(catalogueOrder) === JSON.stringify(OrderByMap['priceDesc'])}
            aria-label='price desc'
          >
            {t('cataloguePage/orderBy/priceDesc')}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
    </div>
  );
});

CataloguePageListToolbarOrderingPicker.displayName = 'CataloguePageListToolbarOrderingPicker';

export default CataloguePageListToolbarOrderingPicker;

const orderMultilang = (params: { field: string; locale: string }) => {
  const { field, locale } = params;
  return [field, locale].join('.');
};
const orderMultiprice = (params: { currency: string; field: string; prefixField?: string; priceGroup?: string }) => {
  const { currency, field, prefixField = '_pricing', priceGroup = '_default' } = params;
  return [prefixField, priceGroup, field, currency].join('.');
};

const useOrderByMap = (): Record<
  /* 'recommended' | */ 'nameAsc' | 'skuAsc' | 'priceAsc' | 'priceDesc',
  QueryOrderSettings
> => {
  const locale = useRecoilValue(localeState);
  const currency = useRecoilValue(currencyState);
  const priceGroup = useRecoilValue(customerPriceGroupState);

  return {
    // recommended: {
    //   orderBy: '_ordering._default',
    //   orderDirection: 'asc',
    // },
    nameAsc: {
      orderBy: orderMultilang({ field: 'displayName', locale }),
      orderDirection: 'asc',
    },
    skuAsc: {
      orderBy: 'sku',
      orderDirection: 'asc',
    },
    priceAsc: {
      orderBy: orderMultiprice({ field: 'price', currency, priceGroup }),
      orderDirection: 'asc',
    },
    priceDesc: {
      orderBy: orderMultiprice({ field: 'price', currency, priceGroup }),
      orderDirection: 'desc',
    },
  };
};
